// Single typed layer over GA4 (gtag), loaded by Pages/Shared/_TrackingScripts.cshtml (G-EKQPZ94DG9).
// The SPA is hash-routed, so every event and virtual pageview goes through here. Components never call
// window.gtag directly; calls no-op when gtag is absent. See docs/02_areas/analytics.
//
// The param naming which UI fired an event is `ui_source`, never `source`: GA4 treats `source` as the
// traffic-acquisition param and rewrites the session's source. Server pages use `data-ui-source`.

type Params = Record<string, string | number | boolean | undefined>;

// GA4 recommended names get built-in reports; keep custom names snake_case (docs/02_areas/analytics/01).
export type AnalyticsEvent =
  | 'select_opening'
  | 'open_game'
  | 'view_item'
  | 'star_game'
  | 'outbound_click'
  | 'filter_games'
  | 'search'
  | 'clear_filters'
  | 'import_start'
  // After a platform import: found:'yes' (games landed) or found:'none' (empty window). Pairs with
  // import_start.
  | 'import_result'
  // Scoresheet reader funnel: interest = opened the photo tab; read = ran an OCR pass.
  | 'import_photo_interest'
  | 'import_photo_read'
  | 'evaluate_game'
  // Tapped the cross-game pattern link on a move (params: pattern, ui_source).
  | 'select_pattern'
  // Chose a time-control speed category in the games filter (params: speed, ui_source).
  | 'select_speed'
  // Added a pattern tag to a move or game (params: scope 'move'|'game', theme).
  | 'user_tag_add'
  // Played a position from your own game out against the engine: start (params: ui_source
  // 'repair'|'game', level) and result (params: outcome 'won'|'lost'|'drawn', level).
  | 'play_out_start'
  | 'play_out_result'
  // /try activation: the repair overlay opened on the visitor's own mistake (params: games, mistakes).
  // With import_start/import_result, the whole front-door funnel.
  | 'try_complete'
  // The end of /try: the sign-up card showed (params: outcome 'done'|'nothing', mistakes) and its
  // button was pressed (params: outcome, signed_in).
  | 'try_offer_seen'
  | 'try_signup_click'
  // The signed-out home page's "fix one move" board: a sample blunder loaded (shown), and the
  // visitor answered it (result: found|missed|shown).
  | 'home_fix_shown'
  | 'home_fix_answer'
  // The one-mistake share page (/blunder/{site}/{username}): started, answered (result:
  // found|missed|gave_up), and the button to /try pressed. Params: site and result only, never the
  // username.
  | 'blunder_page_start'
  | 'blunder_page_answer'
  | 'blunder_page_cta_click'
  // Every repair attempt, and the subset that were right (params: ui_source 'try'|'app', solved,
  // hint_used, move_type). Fired for guests too: CorrectionAttempt is only stored for signed-in users,
  // so GA is the only count of guest repairs.
  | 'repair_attempt'
  | 'repair_solved'
  // Learn library: marked read (params: slug, read), saved a mark (params: slug, kind
  // 'highlight'|'note'|'comment'), asked the tutor (params: slug, with_quote), submitted a quiz
  // (params: slug, score, total).
  | 'learn_mark_read'
  | 'learn_annotate'
  | 'learn_tutor_ask'
  | 'learn_quiz_submit'
  | 'donate_click'
  | 'login'
  | 'sign_up';

function gtagSafe(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === 'function') {
    w.gtag(...args);
  }
}

// GA4 reads these as traffic-acquisition params; catch them instead of trusting review.
const RESERVED_KEYS = new Set(['source', 'medium', 'campaign']);

function clean(params: Params): Params {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => {
        if (!RESERVED_KEYS.has(k)) return [k, v];
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`analytics: param "${k}" is read by GA4 as traffic source; sent as "ui_${k}"`);
        }
        return [`ui_${k}`, v];
      }),
  );
}

export const analytics = {
  // Virtual pageview for SPA state that deserves to count as a page (docs/02_areas/analytics/02).
  pageview(path: string, title?: string): void {
    gtagSafe('event', 'page_view', clean({ page_path: path, page_title: title }));
  },
  track(event: AnalyticsEvent, params: Params = {}): void {
    gtagSafe('event', event, clean(params));
  },
};

// Small helper mirroring OpeningTaxonomy.Slugify for virtual pageview paths.
export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
